import React, {useEffect, useState} from 'react';
import {useHistory} from 'react-router-dom';
import moment from 'moment';
import {useChangelog} from '../context/ChangelogContext';
import ChangelogBadge from './ChangelogBadge';
import KeyboardArrowDownRounded from '@mui/icons-material/KeyboardArrowDownRounded';
import PublicRounded from '@mui/icons-material/PublicRounded';
import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded';
import BuildRounded from '@mui/icons-material/BuildRounded';
import RemoveCircleOutlineRounded from '@mui/icons-material/RemoveCircleOutlineRounded';
import '../styles/changelog.css';

const TYPE_FILTERS = [
  {key: 'all', label: 'All', icon: PublicRounded},
  {key: 'feature', label: 'Features', icon: AutoAwesomeRounded},
  {key: 'improvement', label: 'Improvements', icon: BuildRounded},
  {key: 'retired', label: 'Retired', icon: RemoveCircleOutlineRounded},
];

const ChangelogPage = () => {
  const {allEntries, allLoading} = useChangelog();
  const [expandedMonths, setExpandedMonths] = useState({});
  const [activeFilter, setActiveFilter] = useState('all');
  const history = useHistory();

  // Filter entries by type tag
  const filteredEntries = activeFilter === 'all'
    ? allEntries
    : allEntries.filter((entry) => entry.tags && entry.tags.includes(activeFilter));

  // Group entries by month
  const groupedEntries = filteredEntries.reduce((groups, entry) => {
    const date = entry.publishedAt?.toDate ? entry.publishedAt.toDate() : new Date(entry.publishedAt);
    const key = moment(date).format('MMMM YYYY');
    if (!groups[key]) groups[key] = [];
    groups[key].push(entry);
    return groups;
  }, {});

  const monthKeys = Object.keys(groupedEntries);

  // Expand only the latest month by default
  useEffect(() => {
    if (monthKeys.length > 0 && Object.keys(expandedMonths).length === 0) {
      const initial = {};
      monthKeys.forEach((key, i) => { initial[key] = i === 0; });
      setExpandedMonths(initial);
    }
  }, [monthKeys.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleMonth = (month) => {
    setExpandedMonths((prev) => ({...prev, [month]: !prev[month]}));
  };

  const getTimestamp = (entry) => {
    if (!entry.publishedAt) return '';
    const date = entry.publishedAt?.toDate ? entry.publishedAt.toDate() : new Date(entry.publishedAt);
    return moment(date).fromNow();
  };

  if (allLoading) {
    return (
      <div className="changelog-page">
        <h1 className="changelog-page-title">Changelog</h1>
        <div className="changelog-loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="changelog-page">
      <h1 className="changelog-page-title">Changelog</h1>

      <div className="changelog-filters">
        {TYPE_FILTERS.map(({key, label, icon: Icon}) => (
          <button
            key={key}
            className={`btn btn-sm btn-outline-secondary ${activeFilter === key ? 'active' : ''}`}
            onClick={() => setActiveFilter(key)}
          >
            <Icon className="changelog-filter-icon" />
            <span>{label}</span>
          </button>
        ))}
      </div>

      {monthKeys.length === 0 && (
        <p className="changelog-empty">No changelog entries yet.</p>
      )}

      <div key={activeFilter}>
      {monthKeys.map((month) => (
        <div key={month} className="changelog-month-section">
          <div
            className="changelog-month-header"
            role="button"
            tabIndex={0}
            onClick={() => toggleMonth(month)}
            onKeyDown={(e) => { if (e.key === 'Enter') toggleMonth(month); }}
          >
            <h2 className="changelog-month-title">{month}</h2>
            <KeyboardArrowDownRounded
              className={`changelog-month-icon ${expandedMonths[month] ? 'expanded' : ''}`}
            />
          </div>

          <div className={`changelog-month-entries-wrapper ${expandedMonths[month] ? 'open' : ''}`}>
            <div className="changelog-month-entries">
              {groupedEntries[month].map((entry, index) => (
                <div
                  key={entry.id}
                  className="changelog-entry changelog-entry-clickable changelog-entry-animated"
                  style={{'--entry-index': index}}
                  role="button"
                  tabIndex={0}
                  onClick={() => history.push(`/changelog/${entry.id}`)}
                  onKeyDown={(e) => { if (e.key === 'Enter') history.push(`/changelog/${entry.id}`); }}
                >
                  <div className="changelog-entry-header">
                    <span className="changelog-entry-time">{getTimestamp(entry)}</span>
                    {entry.tags && entry.tags.length > 0 && (
                      <div className="changelog-entry-badges">
                        {entry.tags.map((tag) => (
                          <ChangelogBadge key={tag} tag={tag} />
                        ))}
                      </div>
                    )}
                  </div>
                  <h3 className="changelog-entry-title">{entry.title}</h3>
                  {entry.repository && (
                    <span className="changelog-entry-repo">{entry.repository}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
      </div>
    </div>
  );
};

export default ChangelogPage;
